import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronLeft, Download, Link2, Loader2, Share2 } from 'lucide-react';
import * as api from '../../lib/api';
import { suggestEmail } from '../wifey/slides';
import { saveBlob } from '../wifey/exporter';
import { ANSWERS, AREAS, QUESTIONS, TOTAL, isComplete, scoreQuiz } from './quizContent';
import ShareCard from './ShareCard';

const STORE = 'quiz_v1';            // answers, on this device only
const UNLOCKED = 'quiz_unlocked';   // the confirmed email on this device
const PENDING = 'quiz_pending';     // the email we are waiting to hear back on
const POLL_MS = 8000;
const POLL_STOP_MS = 15 * 60 * 1000;
const PAGE_URL = 'sirati.co/marriage-readiness-quiz';

const read = (key) => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } };
const drop = (key) => { try { localStorage.removeItem(key); } catch { /* storage unavailable: the page still works */ } };

const savedAnswers = () => {
  const saved = read(STORE);
  const out = {};
  QUESTIONS.forEach((q) => { const v = saved?.answers?.[q.id]; if (v >= 1 && v <= 4) out[q.id] = v; });
  return out;
};
const firstUnanswered = (answers) => Math.max(0, QUESTIONS.findIndex((q) => !answers[q.id]));

const BAND_LABEL = { strong: 'Strong', growing: 'Growing', foundation: 'Just starting' };

const Crescent = ({ id }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="w-7 h-7">
    <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#4AADA8" /><stop offset="1" stopColor="#A99CC8" /></linearGradient></defs>
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" fill={`url(#${id})`} />
  </svg>
);

/** A photo from /public/quiz. If the file is missing the block falls back to a soft gradient, so the page never breaks. */
const Photo = ({ file, alt, emoji, className = '' }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div aria-hidden="true" className={`${className} bg-gradient-to-br from-primary/30 to-secondary/40 flex items-center justify-center text-6xl`}>{emoji}</div>;
  }
  return <img src={`${process.env.PUBLIC_URL}/quiz/${file}`} alt={alt} loading="lazy" onError={() => setFailed(true)} className={`${className} object-cover`} />;
};

const QuizPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();

  const [answers, setAnswers] = useState(savedAnswers);
  const [view, setView] = useState(() => (isComplete(savedAnswers()) ? 'result' : 'start'));
  const [step, setStep] = useState(() => firstUnanswered(savedAnswers()));
  const [announce, setAnnounce] = useState('');

  const [unlocked, setUnlocked] = useState(() => read(UNLOCKED) || '');
  const [gate, setGate] = useState(() => (read(UNLOCKED) ? 'done' : 'ask')); // ask | sent | done
  const [email, setEmail] = useState(() => read(PENDING) || '');
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [waitNote, setWaitNote] = useState('');
  const [timedOut, setTimedOut] = useState(false);
  const [justConfirmed, setJustConfirmed] = useState(false);
  const [shareNote, setShareNote] = useState('');
  const [saving, setSaving] = useState(false);

  const headingRef = useRef(null);
  const cardRef = useRef(null);
  const advanceTimer = useRef(null);

  const result = useMemo(() => (isComplete(answers) ? scoreQuiz(answers) : null), [answers]);
  const question = QUESTIONS[step];
  const area = question ? AREAS.find((a) => a.id === question.areaId) : null;
  const areaStart = question ? QUESTIONS.findIndex((q) => q.areaId === question.areaId) : 0;

  useEffect(() => {
    const previous = document.title;
    document.title = 'The Single Muslim Marriage Readiness Quiz | Sirati';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  // Once the email is confirmed the stage appears: move focus to it and say so
  useEffect(() => {
    if (view === 'result' && gate === 'done' && unlocked) {
      headingRef.current?.focus({ preventScroll: true });
      window.scrollTo({ top: 0 });
      setAnnounce('Your stage and breakdown are ready.');
    }
  }, [view, gate, unlocked]);

  // Keyboard and screen reader users land on the new question; sighted users get a calm scroll to the top
  useEffect(() => {
    if (view === 'quiz') {
      headingRef.current?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      setAnnounce(`Question ${step + 1} of ${TOTAL}`);
    } else if (view === 'result') {
      window.scrollTo({ top: 0 });
      headingRef.current?.focus({ preventScroll: true });
      setAnnounce('Your results are ready.');
    }
  }, [view, step, reduce]);

  /* ───────────── questions ───────────── */
  const begin = () => {
    const next = firstUnanswered(answers);
    setStep(isComplete(answers) ? 0 : next);
    setView('quiz');
  };

  const choose = (points) => {
    const next = { ...answers, [question.id]: points };
    setAnswers(next);
    write(STORE, { answers: next });
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      if (step >= TOTAL - 1) setView('result');
      else setStep(step + 1);
    }, reduce ? 0 : 220);
  };

  const back = () => {
    clearTimeout(advanceTimer.current);
    if (step === 0) setView('start'); else setStep(step - 1);
  };

  const retake = () => {
    drop(STORE);
    setAnswers({});
    setStep(0);
    setView('start');
  };

  /* ───────────── email step ───────────── */
  const unlock = useCallback((address) => {
    const clean = String(address || '').trim().toLowerCase();
    write(UNLOCKED, clean);
    drop(PENDING);
    setUnlocked(clean);
    setWaitNote('');
    setGate('done');
  }, []);

  const check = useCallback(async (manual = false) => {
    const address = (email || read(PENDING) || '').trim();
    if (!address) return;
    try {
      const res = await api.quizStatus(address);
      if (res.data?.confirmed) { unlock(address); return; }
      if (manual) setWaitNote('Not yet. Please tap the link in the email we sent. If you cannot find it, check your spam folder.');
    } catch {
      if (manual) setWaitNote('We could not check just now. Please try again in a moment.');
    }
  }, [email, unlock]);

  // Back from the confirmation link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('confirmed') !== '1') return;
    navigate(location.pathname, { replace: true });
    const pending = read(PENDING);
    if (pending) { setEmail(pending); setGate('sent'); } else { setJustConfirmed(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While waiting: check every few seconds, and the moment the tab is looked at again
  useEffect(() => {
    if (gate !== 'sent') return undefined;
    setTimedOut(false);
    check();
    const poll = setInterval(check, POLL_MS);
    const stop = setTimeout(() => { clearInterval(poll); setTimedOut(true); }, POLL_STOP_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(poll);
      clearTimeout(stop);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [gate, check]);

  const submit = async (e, force = false) => {
    if (e) e.preventDefault();
    if (!result) return;
    const address = email.trim();
    if (!force) {
      const fix = suggestEmail(address);
      if (fix) { setSuggestion(fix); return; }
    }
    setSuggestion('');
    setBusy(true);
    setFormError('');
    try {
      const res = await api.quizRequest({ email: address, firstName: firstName.trim(), stage: result.stage.name });
      write(PENDING, address.toLowerCase());
      if (res.data?.status === 'confirmed') unlock(address);
      else { setWaitNote(''); setGate('sent'); }
    } catch (err) {
      setFormError(err?.response?.data?.detail || 'Something went wrong. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  /* ───────────── sharing ───────────── */
  const shareText = result ? `${result.stage.emoji} ${result.stage.share} on the road to marriage. Take the Single Muslim Marriage Readiness Quiz and see your stage:` : '';
  const shareUrl = `https://www.${PAGE_URL}`;

  const share = async () => {
    setShareNote('');
    try {
      if (navigator.share) { await navigator.share({ title: 'The Single Muslim Marriage Readiness Quiz', text: shareText, url: shareUrl }); return; }
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setShareNote('Link copied. Paste it anywhere to share.');
    } catch (err) {
      if (err?.name !== 'AbortError') setShareNote('We could not share from here. Copy the link from your address bar instead.');
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setShareNote('Link copied.'); } catch { setShareNote(`Copy this link: ${shareUrl}`); }
  };

  const saveCard = async () => {
    if (!cardRef.current || saving) return;
    setSaving(true);
    setShareNote('');
    try {
      const { toPng } = await import('html-to-image');
      if (document.fonts?.ready) await document.fonts.ready;
      await toPng(cardRef.current, { width: 1080, height: 1350, pixelRatio: 1 }).catch(() => null);   // warm-up for Safari
      const dataUrl = await toPng(cardRef.current, { width: 1080, height: 1350, pixelRatio: 1 });
      const blob = await (await fetch(dataUrl)).blob();
      saveBlob(blob, 'my-marriage-readiness-stage.png');
    } catch {
      setShareNote('We could not make the picture. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fade = reduce ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.25 } };

  const inputClass = 'w-full px-4 py-3 bg-white/80 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/25 focus:ring-2 focus:ring-primary focus:outline-none text-text-main min-h-[44px]';
  const primaryBtn = 'inline-flex items-center justify-center gap-2 px-7 py-3 bg-primary text-white rounded-full font-medium min-h-[44px] hover:opacity-90 active:scale-[0.98] transition disabled:opacity-60';
  const ghostBtn = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-primary/30 text-primary rounded-full font-medium min-h-[44px] hover:bg-primary/10 active:scale-[0.98] transition disabled:opacity-60';

  /* ───────────── screens ───────────── */
  const renderStart = () => {
    const started = Object.keys(answers).length > 0 && !isComplete(answers);
    return (
      <motion.div key="start" {...fade} className="glass-card rounded-arch-xl overflow-hidden">
        <Photo file="hero.jpg" alt="A Muslim couple standing together by the water" emoji="🌙" className="w-full h-52 sm:h-72" />
        <div className="p-6 sm:p-10">
          <p className="inline-flex items-center gap-2 text-sm font-medium text-secondary bg-secondary/15 rounded-full px-3 py-1 mb-4">For single Muslims, brothers and sisters</p>
          <h1 ref={headingRef} tabIndex={-1} className="font-heading text-3xl sm:text-5xl font-semibold tracking-tight text-text-main outline-none">
            The Single Muslim <span className="text-primary">Marriage Readiness</span> Quiz
          </h1>
          <p className="mt-4 text-base sm:text-lg text-text-main leading-relaxed">
            Which stage are you in on the road to marriage? Answer {TOTAL} quick questions and find out where you are strong, and what to work on next. 🌙
          </p>
          <ul className="mt-5 flex flex-wrap gap-2 text-sm text-text-main" aria-label="About this quiz">
            <li className="px-3 py-1.5 rounded-full bg-primary/10">⏱ About 2 minutes</li>
            <li className="px-3 py-1.5 rounded-full bg-primary/10">🔒 Private: your answers stay on your device</li>
            <li className="px-3 py-1.5 rounded-full bg-primary/10">💜 Free</li>
          </ul>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button type="button" onClick={begin} className={primaryBtn}>
              {started ? 'Continue the quiz' : 'Start the quiz'} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            {started && <button type="button" onClick={retake} className={ghostBtn}>Start over</button>}
          </div>
          <p className="mt-5 text-xs text-text-muted">
            Made by Sirati, a faith-centered community for Muslims preparing for marriage. For single Muslims 18 and over. Not a substitute for advice from a scholar or counselor.
          </p>
        </div>
      </motion.div>
    );
  };

  const renderQuestion = () => (
    <motion.div key={`q-${question.id}`} {...fade} className="glass-card rounded-arch-xl overflow-hidden">
      <div className="relative">
        <Photo file={area.photo.file} alt={area.photo.alt} emoji={area.emoji} className="w-full h-36 sm:h-52" />
        <span className="absolute left-4 bottom-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 text-gray-800 text-sm font-medium">
          <span aria-hidden="true">{area.emoji}</span> {area.name}
        </span>
      </div>
      <div className="p-5 sm:p-8">
        <div className="flex items-center justify-between text-sm text-text-muted mb-2">
          <span>Question {step + 1} of {TOTAL}</span>
          <span>{step - areaStart + 1} of {area.questions.length} in this area</span>
        </div>
        <div className="h-3 bg-white/60 rounded-full overflow-hidden" role="progressbar" aria-label="Quiz progress" aria-valuemin={0} aria-valuemax={TOTAL} aria-valuenow={step + 1}>
          <div className="h-full progress-bar rounded-full transition-all duration-500" style={{ width: `${((step + 1) / TOTAL) * 100}%` }} />
        </div>

        <h2 id="quiz-q" ref={headingRef} tabIndex={-1} className="mt-6 font-heading text-xl sm:text-2xl font-semibold text-text-main leading-snug outline-none">
          {question.text}
        </h2>

        <div role="radiogroup" aria-labelledby="quiz-q" className="mt-5 grid gap-3">
          {ANSWERS.map((a) => {
            const picked = answers[question.id] === a.points;
            return (
              <button
                key={a.points}
                type="button"
                role="radio"
                aria-checked={picked}
                onClick={() => choose(a.points)}
                className={`text-left px-5 py-3 rounded-arch border min-h-[48px] font-medium transition active:scale-[0.99] ${
                  picked ? 'bg-primary text-white border-primary' : 'bg-white/70 dark:bg-white/90 dark:text-gray-800 text-text-main border-primary/25 hover:border-primary hover:bg-primary/10'
                }`}
              >
                {a.label}
              </button>
            );
          })}
        </div>

        <button type="button" onClick={back} className="mt-5 inline-flex items-center gap-1 text-sm text-text-muted hover:text-primary min-h-[44px]">
          <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Back
        </button>
      </div>
    </motion.div>
  );

  const renderGate = () => {
    if (gate === 'sent') {
      return (
        <div className="glass-card rounded-arch-xl p-6 sm:p-8 text-center" role="status">
          <p className="text-4xl" aria-hidden="true">✉️</p>
          <h3 className="mt-2 font-heading text-xl font-semibold text-text-main">Check your inbox</h3>
          <p className="mt-2 text-text-main leading-relaxed">
            We sent a confirmation email to <b>{email}</b>. Tap the link inside and your stage and full breakdown will open here. If you cannot find it, check your spam folder.
          </p>
          {!timedOut && <p className="mt-3 inline-flex items-center gap-2 text-sm text-text-muted"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Waiting for your confirmation</p>}
          {timedOut && <p className="mt-3 text-sm text-text-muted">Still waiting. Once you have tapped the link, press the button below.</p>}
          {waitNote && <p className="mt-3 text-sm text-text-main">{waitNote}</p>}
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <button type="button" onClick={() => check(true)} className={primaryBtn}>I have confirmed, show my results</button>
            <button type="button" onClick={() => { setGate('ask'); setWaitNote(''); }} className={ghostBtn}>Use a different email</button>
          </div>
        </div>
      );
    }
    return (
      <form onSubmit={submit} className="glass-card rounded-arch-xl p-6 sm:p-8" noValidate>
        <h1 ref={headingRef} tabIndex={-1} className="font-heading text-2xl sm:text-3xl font-semibold text-text-main outline-none">Your results are ready 🎉</h1>
        <p className="mt-2 text-text-main leading-relaxed">
          See which stage you are in on the road to marriage, plus your score in each of the 5 areas and one simple action for each. Enter your email and we will send a quick confirmation first.
        </p>
        {justConfirmed && <p className="mt-3 text-sm text-primary font-medium">Your email is confirmed. Enter it again below to open your results.</p>}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="quiz-name" className="block text-sm font-medium text-text-main mb-1">First name (optional)</label>
            <input id="quiz-name" type="text" autoComplete="given-name" maxLength={40} value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="quiz-email" className="block text-sm font-medium text-text-main mb-1">Email</label>
            <input
              id="quiz-email" type="email" autoComplete="email" inputMode="email" required value={email}
              onChange={(e) => { setEmail(e.target.value); setSuggestion(''); }}
              aria-describedby={formError ? 'quiz-error' : undefined} aria-invalid={!!formError} className={inputClass}
            />
          </div>
        </div>
        {suggestion && (
          <p className="mt-3 text-sm text-text-main">
            Did you mean <b>{suggestion}</b>?{' '}
            <button type="button" className="text-primary underline min-h-[44px]" onClick={() => { setEmail(suggestion); setSuggestion(''); }}>Yes, use that</button>{' '}
            <button type="button" className="text-primary underline min-h-[44px]" onClick={() => submit(null, true)}>No, keep mine</button>
          </p>
        )}
        {formError && <p id="quiz-error" role="alert" className="mt-3 text-sm text-red-600">{formError}</p>}
        <button type="submit" disabled={busy || !email.trim()} className={`${primaryBtn} mt-5`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null} Show my results
        </button>
        <p className="mt-3 text-xs text-text-muted">
          By continuing you agree to get emails from Sirati. You can unsubscribe at any time. See our <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>
      </form>
    );
  };

  const renderBreakdown = () => (
    <div className="space-y-4">
      <h3 className="font-heading text-2xl font-semibold text-text-main">Your breakdown 📊</h3>
      {result.areas.map((entry) => {
        const { area: a, score, max, band } = entry;
        const tag = result.balanced ? '' : result.strongest.includes(entry) ? 'Your strongest area' : result.focus.includes(entry) ? 'Your area for growth' : '';
        const text = a.results[band];
        return (
          <section key={a.id} className="glass-card rounded-arch-xl p-5 sm:p-6" aria-labelledby={`area-${a.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 id={`area-${a.id}`} className="font-heading text-lg font-semibold text-text-main"><span aria-hidden="true">{a.emoji}</span> {a.name}</h4>
              <div className="flex items-center gap-2">
                {tag && <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary/20 text-secondary">{tag}</span>}
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/15 text-primary">{BAND_LABEL[band]}</span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 h-3 bg-white/60 rounded-full overflow-hidden" role="img" aria-label={`${score} out of ${max}`}>
                <div className="h-full progress-bar rounded-full" style={{ width: `${(score / max) * 100}%` }} />
              </div>
              <span className="text-sm text-text-muted tabular-nums">{score} of {max}</span>
            </div>
            <p className="mt-3 text-text-main leading-relaxed">{text.text}</p>
            <p className="mt-3 rounded-arch bg-primary/10 px-4 py-3 text-text-main leading-relaxed"><b>Try this:</b> {text.action}</p>
          </section>
        );
      })}

      <section className="glass-card rounded-arch-xl p-6 sm:p-8 text-center" aria-labelledby="quiz-next">
        <h3 id="quiz-next" className="font-heading text-2xl font-semibold text-text-main">Keep going with Sirati 🌙</h3>
        <p className="mt-3 text-text-main leading-relaxed">
          Join Sirati to connect with Muslim singles, journey and grow in community alongside others who are on the same path.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <a href="/" className={primaryBtn}>Join Sirati free <ArrowRight className="w-4 h-4" aria-hidden="true" /></a>
        </div>
      </section>
    </div>
  );

  const renderResult = () => (gate === 'done' && unlocked ? renderStageAndBreakdown() : (
    <motion.div key="locked" {...fade} className="space-y-5">
      {renderGate()}
      <div className="text-center">
        <button type="button" onClick={retake} className="text-sm text-text-muted underline min-h-[44px]">Take the quiz again</button>
      </div>
    </motion.div>
  ));

  const renderStageAndBreakdown = () => (
    <motion.div key="result" {...fade} className="space-y-5">
      <section className="glass-card rounded-arch-xl p-6 sm:p-10 text-center" aria-labelledby="quiz-stage">
        <p className="text-sm font-medium text-text-muted">Your stage on the road to marriage</p>
        <p className="mt-3 text-6xl" aria-hidden="true">{result.stage.emoji}</p>
        <h1 id="quiz-stage" ref={headingRef} tabIndex={-1} className="mt-3 font-heading text-3xl sm:text-5xl font-semibold tracking-tight text-primary outline-none">{result.stage.name}</h1>
        <p className="mt-4 text-base sm:text-lg text-text-main leading-relaxed max-w-xl mx-auto">{result.stage.text}</p>
        {!result.balanced && (
          <div className="mt-4 space-y-1 text-text-main">
            <p>
              {result.strongest.length > 1 ? 'Your strongest areas' : 'Your strongest area'}:{' '}
              <b>{result.strongest.map((e) => `${e.area.emoji} ${e.area.name}`).join(' and ')}</b>
            </p>
            <p>
              {result.focus.length > 1 ? 'Your areas for growth' : 'Your area for growth'}:{' '}
              <b>{result.focus.map((e) => `${e.area.emoji} ${e.area.name}`).join(' and ')}</b>
            </p>
          </div>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={share} className={primaryBtn}><Share2 className="w-4 h-4" aria-hidden="true" /> Share the quiz</button>
          <button type="button" onClick={saveCard} disabled={saving} className={ghostBtn}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />} Save my result picture
          </button>
          <button type="button" onClick={copyLink} className={ghostBtn}><Link2 className="w-4 h-4" aria-hidden="true" /> Copy link</button>
        </div>
        <p role="status" className="mt-3 text-sm text-text-muted min-h-[1.25rem]">{shareNote}</p>
        <p className="mt-1 text-xs text-text-muted">The picture shows only your stage. Send it to a friend who is thinking about marriage and see what stage they get.</p>
      </section>

      {renderBreakdown()}

      <div className="text-center">
        <button type="button" onClick={retake} className="text-sm text-text-muted underline min-h-[44px]">Take the quiz again</button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen feed-ombre-bg relative ph-no-capture" lang="en">
      <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #4AADA8, #A99CC8)' }} aria-hidden="true" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <header className="flex items-center justify-between mb-6">
          <a href="/" aria-label="Sirati home" className="inline-flex items-center gap-2 font-heading text-xl font-semibold tracking-wide text-primary min-h-[44px]">
            <Crescent id="quiz-crescent" /> Sirati
          </a>
          <a href="/" className="text-sm text-text-muted hover:text-primary min-h-[44px] inline-flex items-center">Join free</a>
        </header>

        <main>
          <div className="sr-only" aria-live="polite">{announce}</div>
          <AnimatePresence mode="wait">
            {view === 'start' && renderStart()}
            {view === 'quiz' && question && renderQuestion()}
            {view === 'result' && result && renderResult()}
          </AnimatePresence>
        </main>

        <footer className="mt-10 text-center text-xs text-text-muted">
          &copy; {new Date().getFullYear()} Sirati. All Rights Reserved. &middot; <a href="/privacy" className="underline">Privacy</a> &middot; <a href="/terms" className="underline">Terms</a>
        </footer>
      </div>

      {result && <ShareCard ref={cardRef} stage={result.stage} url={PAGE_URL} />}
    </div>
  );
};

export default QuizPage;
